<script>
import VueDataTableFunctions from "./VueDataTableFunctions";
import {TableHeaderManager} from "@/assets/plugins/vue-data-table/table-header-manager";
import cloneDeep from "@/assets/plugins/mps-common/expansions/clone/cloneDeep";

export default {
    name: "VDataTableProps",
    mixins: [VueDataTableFunctions],
    props: {
        /**
         * 여기까지 `custom` props 영역입니다.
         */
        fixedItemHeaders: {
            type: Object,
            default: () => {
            },
        },
        showFixedItem: {
            type: Boolean,
            default: false,
        },
        showUtil: {
            type: Boolean,
            default: false,
        },
        /**
         * 여기부터 v-data-table Props 영역입니다.
         */
        calculateWidths: undefined,
        caption: undefined,
        customFilter: undefined,
        customGroup: undefined,
        // customSort: undefined,
        dark: undefined,
        dense: undefined,
        disableFiltering: undefined,
        disablePagination: undefined,
        disableSort: {
            type: Boolean,
            default: false,
        },
        expandIcon: undefined,
        expanded: undefined,
        fixedHeader: {
            type: Boolean,
            default: true,
        },
        footerProps: undefined,
        groupBy: {
            type: [String, Array],
            default: () => [],
        },
        groupDesc: {
            type: [Boolean, Array],
            default: () => [],
        },
        headerProps: undefined,
        headers: {
            type: Array,
            default: () => [],
        },
        headersLength: undefined,
        height: {
            type: [Number, String],
            default: undefined,
        },
        hideDefaultFooter: {
            type: Boolean,
            default: true,
        },
        hideDefaultHeader: undefined,
        itemClass: {
            type: [Function, Array],
            default: undefined,
        },
        items: undefined,
        itemsPerPage: {
            type: Number,
            default: 10,
        },
        light: undefined,
        loaderHeight: undefined,
        loading: {
            type: Boolean,
            default: false,
        },
        loadingText: undefined,
        locale: undefined,
        mobileBreakpoint: undefined,
        multiSort: {
            type: Boolean,
            default: false,
        },
        mustSort: undefined,
        noDataText: {
            type: String,
            default: "No Data",
        },
        noResultsText: undefined,
        options: undefined,
        page: undefined,
        search: {
            type: String,
            default: undefined,
        },
        selectableKey: undefined,
        serverItemsLength: undefined,
        showExpand: {
            type: Boolean,
            default: false,
        },
        showGroupBy: {
            type: Boolean,
            default: false,
        },
        singleExpand: {
            type: Boolean,
            default: false,
        },
        showSelect: {
            type: Boolean,
            default: false,
        },
        singleSelect: {
            type: Boolean,
            default: false,
        },
        itemKey: {
            type: String,
            default: undefined,
        },
        sortBy: {
            type: [String, Array],
            default: () => [],
        },
        sortDesc: {
            type: [Boolean, Array],
            default: () => [],
        },
        value: {
            type: Array,
            default: () => [],
        },
        validateModel: {
            type: Function,
            default: undefined,
        },
    },
    data() {
        return {
            model: this.value,
            tableHeaderManager: new TableHeaderManager(),
        };
    },
    computed: {
        fixedItemHeadersSafe() {
            return this.fixedItemHeaders || {};
        },

        /**
         * 보이지 않을 헤더를 제거합니다.
         */
        headersExclude() {
            return this.headers.filter(e => !e.exclude || !e.exclude());
        },

        /**
         * 유틸 헤더가 없다면 value를 포함시켜 주기 위해 data-table-select 헤더가 있는지 검사합니다.
         */
        hasSelectHeader() {
            return !!this.headersExclude.find(e => e.value === "data-table-select");
        },

        /**
         * 유틸 헤더가 없다면 value를 포함시켜 주기 위해 data-table-select 헤더가 있는지 검사합니다.
         */
        hasExpandHeader() {
            return !!this.headersExclude.find(e => e.value === "data-table-expand");
        },

        /**
         * 유틸 헤더가 없다면 value를 포함시켜 주기 위해 유틸헤더가 있는지 검사합니다.
         */
        // hasUtilHeader() {
        //     return !!this.headersExclude.find(e => e.value === "data-table-util");
        // },

        /**
         * 슬랏 추가 등 부가적인 작업을 합니다.
         *
         * 커스텀 헤더와 v-data-table 의 컬럼 수와 순서를 맞춰야 하기 때문에
         * 헤더 추가 slot 생성에 주의합니다.
         */
        headersComputed() {
            const headers = [];

            // data-table-select 영역을 생성합니다.
            if (this.showSelect && !this.hasSelectHeader) {
                const selectHeader = {value: "data-table-select", isSelect: true, align: "center",};
                headers.push(selectHeader);
            }
            // 원본 헤더 붙임
            headers.push(...this.headersExclude);
            // data-table-expand 영역을 생성합니다.
            if (this.showExpand && !this.hasExpandHeader) {
                const expandHeader = {value: "data-table-expand", isExpand: true, align: "center",};
                headers.push(expandHeader);
            }
            // data-table-util 영역을 생성합니다.
            // if (this.showUtil && !this.hasUtilHeader) {
            //     const utilHeader = {value: "data-table-util", isUtil: true, align: "center",};
            //     headers.push(utilHeader);
            // }
            return headers;
        },

        /**
         * item.[name] slot 이름 배열을 반환합니다.
         *
         * 이 배열은 item slot 을 대체하는데 사용합니다.
         */
        itemSlotNames() {
            return this.tableHeaderManager.endNodes
                // expand 는 현재 v-data-table 의 expand 기능을 그대로 사용하기 때문에 필터링합니다.
                // select 는 현재 v-data-table 의 expand 기능을 그대로 사용하기 때문에 필터링합니다.
                .filter(e => e.raw.value !== "data-table-expand")
                .filter(e => e.raw.value !== "data-table-select")
                .map(e => {
                    return {
                        wrappingSlotName: "item." + e.prefixRaw.value,
                        itemSlotName: "item." + e.raw.value,
                    }
                });
        },

        isOneMoreChecked() {
            return this.model.length > 0 && !this.isAllChecked;
        },

        isAllChecked() {
            return this.model.length > 0 && this.model.length === this.list.length;
        },
    },
    watch: {
        /**
         * 헤더 데이터가 변경되었습니다.
         * 다시 재 트리 노드화 합니다.
         */
        headersComputed() {
            this.tableHeaderManager.setHeaders(this.headersComputed, 'model');
        },

        value() {
            if (this.equals()) return; // 실질적으로 같은 배열입니다.
            this.model = this.wrap(this.value);
        },

        model() {
            this.$emit('input', this.model.map(e => e.model));
        },
    },
    created() {
        // select, util, expand 등을 적용한 헤더 배열을 tableHeaderManager 에 넣습니다.
        // 트리 노드화 합니다.
        this.tableHeaderManager.setHeaders(this.headersComputed, 'model');
    },
    mounted() {
    },
    methods: {
        updateCurrentItems(v) {
            this.currentItems = v;
            this.$emit("current-items", v);
        },

        /**
         * 렌더링 최적화를 위해서 내부적으로 model.이라는 접두사를 이용해
         * 원본 아이템에 접근할 수 있습니다. 외부에서는 몰라도 되므로 내부의 접두사가 붙은 model 목록과
         * 외부의 원본 목록을 v-model 로 구현할 수 있도록, 두 배열을 비교하는 함수를 사용합니다.
         */
        equals() {
            if (!this.model && !this.value) return true;
            if (this.model && !this.value) return false;
            if (!this.model && this.value) return false;
            const rawList = this.model.map(e => e.model);
            return _.isEqual(rawList, this.value);
        },

        toggleModel() {
            if (this.model.length === 0) { /* do nothing */
                this.onClickCheckAll();
            } else if (this.isOneMoreChecked || this.isAllChecked) { /* intermediate */
                this.onClickUncheckAll();
            }
        },

        /**
         * 체크된 아이템을 모두 해제합니다.
         */
        onClickUncheckAll() {
            this.model = [];
        },

        /**
         * 모든 아이템을 체크합니다.
         */
        onClickCheckAll() {
            this.model = this.list;
        },

        /**
         * 아이템이 선택되었습니다. (data-table-select)
         */
        onInput(v) {
            if (v && this.validateModel && !this.validateModel(v.map(e => e.model))) {
                this.model.push({model: null,});
                this.$nextTick(() => {
                    this.model.pop();
                });
                return;
            }
            this.model = v;
        },
    },
}
</script>
